/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import { snakeCase } from 'lodash';

import { Chip, Typography2, breakpoints } from '@coursera/cds-core';

import withSingleTracked from 'bundles/common/components/withSingleTracked';

import _t from 'i18n!nls/coursera-plus-landing-page';

const TrackedChip = withSingleTracked({ type: 'BUTTON' })(Chip);

type Props = {
  updateDomain: (domainId: string) => void;
  domains: Array<{ id: string; name: string }>;
};

const styles = {
  container: css`
    display: flex;
    flex-direction: row;
    gap: var(--cds-spacing-200);
    justify-content: center;
    align-items: center;
    margin-top: var(--cds-spacing-150);

    ${breakpoints.down('sm')} {
      flex-direction: column;
      gap: var(--cds-spacing-50);
    }

    & > div {
      justify-content: center;
    }

    label {
      margin-top: var(--cds-spacing-50);
    }

    button.cds-Chip-root {
      min-height: unset;
      margin: var(--cds-spacing-50) calc(var(--cds-spacing-50) / 2) 0;
      border: none;
      border-radius: var(--cds-border-radius-50);
      background-color: var(--cds-color-neutral-disabled-weak);
      color: var(--cds-color-emphasis-primary-content-default);
    }

    button.cds-Chip-root:hover {
      background-color: var(--cds-color-neutral-stroke-primary-weak);
    }

    button.cds-Chip-root.cds-buttonBase-focusVisible::after {
      border-radius: var(--cds-border-radius-50);
    }
  `,
};

const DomainChips = ({ updateDomain, domains }: Props) => {
  if (!domains) return null;
  return (
    <div css={styles.container}>
      <Typography2 component="label" variant="actionSecondary">
        {_t('Popular')}
      </Typography2>
      <div>
        {domains.map((domain) => (
          <TrackedChip
            trackingName={`coursera_plus_domain_chip_${snakeCase(domain.id)}`}
            key={domain.id}
            onClick={() => {
              updateDomain(domain.id);
            }}
            aria-checked={undefined}
            aria-label={_t('#{domainName}, add to search', { domainname: domain.name })}
          >
            {domain.name}
          </TrackedChip>
        ))}
      </div>
    </div>
  );
};

export default DomainChips;
